import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2, RefreshCw, XCircle, ChevronDown, ChevronUp,
  FileText, User, Package, Calculator, Clock, Search, Filter,
  ChevronLeft, ChevronRight, History, Eye,
} from "lucide-react";
import api, { getStoredUser } from "../api";

/* ── helpers ────────────────────────────────────────────────────────────────── */
function getCurrentUser() {
  return getStoredUser();
}
const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";

const STATUS_COLORS = {
  PENDING_APPROVAL: { bg: "#fef3c7", color: "#b45309", label: "Pending" },
  APPROVED:         { bg: "#d1fae5", color: "#065f46", label: "Approved" },
  REJECTED:         { bg: "#fee2e2", color: "#b91c1c", label: "Rejected" },
  DRAFT:            { bg: "#f1f5f9", color: "#475569", label: "Draft" },
  SENT:             { bg: "#dbeafe", color: "#1d4ed8", label: "Sent" },
  SENT_TO_CLIENT:   { bg: "#dbeafe", color: "#1d4ed8", label: "Sent" },
  CLIENT_ACCEPTED:  { bg: "#d1fae5", color: "#047857", label: "Accepted" },
  CLIENT_REJECTED:  { bg: "#fee2e2", color: "#b91c1c", label: "Rejected" },
  EXPIRED:          { bg: "#f1f5f9", color: "#9ca3af", label: "Expired" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_COLORS[status] || { bg: "#f1f5f9", color: "#475569", label: status };
  return (
    <span style={{
      display: "inline-block", padding: "3px 12px", borderRadius: 20,
      fontSize: 11, fontWeight: 700, background: cfg.bg, color: cfg.color,
      whiteSpace: "nowrap",
    }}>
      {cfg.label || status?.replace(/_/g, " ")}
    </span>
  );
}

/* ── Reject Modal ───────────────────────────────────────────────────────────── */
function RejectModal({ open, onClose, onConfirm, loading }) {
  const [reason, setReason] = useState("");
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, padding: 28, width: 420, maxWidth: "94vw",
        boxShadow: "0 24px 64px rgba(0,0,0,.2)",
      }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 17, fontWeight: 800, color: "#1e293b" }}>
          Reject Quotation
        </h3>
        <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 5, display: "block" }}>
          Rejection Reason
        </label>
        <textarea
          rows={3}
          placeholder="Enter reason for rejection..."
          value={reason}
          onChange={e => setReason(e.target.value)}
          style={{
            width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10,
            fontSize: 14, resize: "vertical", outline: "none", boxSizing: "border-box", color: "#000",
          }}
        />
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onClose} className="qa-btn qa-btn-outline">Cancel</button>
          <button
            onClick={() => { onConfirm(reason); setReason(""); }}
            disabled={loading}
            className="qa-btn qa-btn-danger"
          >
            {loading ? "Rejecting..." : "Reject Quotation"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Quotation Detail Panel (inline expandable) ────────────────────────────── */
function QuotationDetailPanel({ row, onApprove, onReject, actioningId, isHistory }) {
  if (!row) return null;
  const items = row.items || [];

  return (
    <div style={{ padding: "0 24px 24px" }}>
      {/* Customer + Quotation Info */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        {/* Customer Info */}
        <div style={{
          background: "#f8fafc", borderRadius: 12, padding: 18,
          border: "1px solid #e2e8f0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <User size={15} color="#6366f1" />
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Customer Details</span>
          </div>
          <div style={{ display: "grid", gap: 6, fontSize: 13 }}>
            <InfoRow label="Name" value={row.customer_name} />
            <InfoRow label="Email" value={row.customer_email} />
            <InfoRow label="Phone" value={row.customer_phone} />
            <InfoRow label="GSTIN" value={row.customer_gstin} />
            {row.customer_address && <InfoRow label="Address" value={row.customer_address} />}
          </div>
        </div>

        {/* Quotation Info */}
        <div style={{
          background: "#f8fafc", borderRadius: 12, padding: 18,
          border: "1px solid #e2e8f0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <FileText size={15} color="#6366f1" />
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Quotation Info</span>
          </div>
          <div style={{ display: "grid", gap: 6, fontSize: 13 }}>
            <InfoRow label="Quotation #" value={row.quotation_no} />
            <InfoRow label="Subject" value={row.subject} />
            <InfoRow label="Version" value={`V${row.current_version || 1}`} />
            <InfoRow label="Salesperson" value={row.salesperson} />
            <InfoRow label="Created" value={fmtDate(row.created_at)} />
            <InfoRow label="Valid Until" value={fmtDate(row.valid_until)} />
            {isHistory && row.approved_by && <InfoRow label="Decided By" value={row.approved_by} />}
            {isHistory && row.approved_at && <InfoRow label="Decision Date" value={fmtDate(row.approved_at)} />}
            {row.rejection_reason && <InfoRow label="Rejection Reason" value={row.rejection_reason} highlight />}
          </div>
        </div>
      </div>

      {/* Line Items Table */}
      <div style={{
        background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0",
        overflow: "hidden", marginBottom: 20,
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 8, padding: "14px 18px",
          borderBottom: "1px solid #e2e8f0", background: "#f8fafc",
        }}>
          <Package size={15} color="#6366f1" />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
            Line Items ({items.length})
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f1f5f9" }}>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Design</th>
                <th style={thStyle}>Metal</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Weight (g)</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Qty</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Unit Price</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Making</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Stone</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Other</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item.id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={tdStyle}>{idx + 1}</td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: "#1e293b" }}>{item.design_name || item.design_code}</div>
                    {item.design_code && item.design_name && (
                      <div style={{ fontSize: 11, color: "#94a3b8" }}>{item.design_code}</div>
                    )}
                    {item.special_requirements && (
                      <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 2 }}>
                        Note: {item.special_requirements}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>{item.metal_type || "-"}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>{item.metal_weight_g || "-"}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>{item.quantity}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>₹{fmt(item.unit_price)}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>₹{fmt(item.making_charges)}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>₹{fmt(item.stone_charges)}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>₹{fmt(item.other_charges)}</td>
                  <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>₹{fmt(item.total_price)}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan="10" style={{ ...tdStyle, textAlign: "center", color: "#94a3b8" }}>
                    No line items
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Financial Summary + Terms/Notes */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        {/* Financial Summary */}
        <div style={{
          background: "linear-gradient(135deg, #1e1b4b, #312e81)", borderRadius: 14,
          padding: "20px 24px", color: "#fff",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <Calculator size={15} />
            <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Financial Summary</span>
          </div>
          <TotalsRow label="Subtotal" value={`₹${fmt(row.subtotal)}`} />
          <TotalsRow label={`Discount (${row.discount_pct || 0}%)`} value={`-₹${fmt(row.discount_amount)}`} />
          <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,.2)", margin: "8px 0" }} />
          <TotalsRow label={`GST (${row.tax_pct || 0}%)`} value={`+₹${fmt(row.tax_amount)}`} />
          <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,.2)", margin: "8px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 20, fontWeight: 800, paddingTop: 4, color: "#fff" }}>
            <span style={{ color: "#fff" }}>TOTAL</span>
            <span style={{ color: "#fff" }}>₹{fmt(row.total_amount)}</span>
          </div>
        </div>

        {/* Terms & Notes */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {row.terms_and_conditions && (
            <div style={{
              background: "#f8fafc", borderRadius: 12, padding: 18,
              border: "1px solid #e2e8f0", flex: 1,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 8 }}>
                Terms & Conditions
              </div>
              <p style={{ fontSize: 13, color: "#334155", margin: 0, lineHeight: 1.6 }}>
                {row.terms_and_conditions}
              </p>
            </div>
          )}
          {row.internal_notes && (
            <div style={{
              background: "#fffbeb", borderRadius: 12, padding: 18,
              border: "1px solid #fde68a", flex: 1,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 8 }}>
                Internal Notes (not visible to customer)
              </div>
              <p style={{ fontSize: 13, color: "#78350f", margin: 0, lineHeight: 1.6 }}>
                {row.internal_notes}
              </p>
            </div>
          )}
          {!row.terms_and_conditions && !row.internal_notes && (
            <div style={{
              background: "#f8fafc", borderRadius: 12, padding: 18,
              border: "1px solid #e2e8f0", flex: 1,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#94a3b8", fontSize: 13,
            }}>
              No terms or notes attached.
            </div>
          )}
        </div>
      </div>

      {/* Approve / Reject Actions - only for pending */}
      {!isHistory && (
        <div style={{
          display: "flex", gap: 12, justifyContent: "flex-end",
          paddingTop: 16, borderTop: "1px solid #e2e8f0",
        }}>
          <button
            onClick={() => onReject(row)}
            disabled={actioningId === row.id}
            className="qa-btn qa-btn-danger"
            style={{ padding: "10px 24px" }}
          >
            <XCircle size={15} /> Reject
          </button>
          <button
            onClick={() => onApprove(row)}
            disabled={actioningId === row.id}
            className="qa-btn qa-btn-success"
            style={{ padding: "10px 24px" }}
          >
            <CheckCircle2 size={15} /> Approve
          </button>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, highlight }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
      <span style={{ color: "#64748b", fontWeight: 500 }}>{label}</span>
      <span style={{
        color: highlight ? "#dc2626" : "#1e293b", fontWeight: 600,
        textAlign: "right", maxWidth: "60%",
      }}>
        {value}
      </span>
    </div>
  );
}

function TotalsRow({ label, value, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 14 }}>
      <span style={{ opacity: 0.85, color: "#fff" }}>{label}</span>
      <span style={{ fontWeight: 600, color: "#fff" }}>{value}</span>
    </div>
  );
}

const thStyle = {
  padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700,
  color: "#64748b", textTransform: "uppercase", letterSpacing: ".4px",
  borderBottom: "2px solid #e2e8f0",
};
const tdStyle = {
  padding: "10px 14px", color: "#1e293b", verticalAlign: "top",
};

/* ── Main Component ─────────────────────────────────────────────────────────── */
export default function QuotationApprovals() {
  const [activeTab, setActiveTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);

  // History state
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyFilter, setHistoryFilter] = useState("ALL");
  const [historySearch, setHistorySearch] = useState("");
  const [historyDateFrom, setHistoryDateFrom] = useState("");
  const [historyDateTo, setHistoryDateTo] = useState("");
  const perPage = 15;

  const currentUser = useMemo(() => getCurrentUser(), []);
  const isAdmin = Boolean(
    currentUser?.is_admin ||
      currentUser?.role === "org_admin" ||
      (currentUser?.access_matrix || []).some((entry) => entry.role === "org_admin")
  );

  /* ── Load pending approvals ──────────────────────────────────────────────── */
  const loadPending = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/quotation-approvals");
      setRows(res.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Failed to load quotation approvals.");
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── Load history ────────────────────────────────────────────────────────── */
  const loadHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("per_page", perPage);
      if (historyFilter !== "ALL") params.set("status", historyFilter);
      if (historySearch.trim()) params.set("search", historySearch.trim());
      if (historyDateFrom) params.set("date_from", historyDateFrom);
      if (historyDateTo) params.set("date_to", historyDateTo);

      const res = await api.get(`/admin/quotation-history?${params.toString()}`);
      setHistoryRows(res.data?.data || []);
      setHistoryTotal(res.data?.total || 0);
      setHistoryPage(res.data?.page || 1);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Failed to load history.");
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFilter, historySearch, historyDateFrom, historyDateTo]);

  useEffect(() => {
    if (isAdmin) loadPending();
  }, [isAdmin, loadPending]);

  useEffect(() => {
    if (isAdmin && activeTab === "history") loadHistory(1);
  }, [isAdmin, activeTab, loadHistory]);

  /* ── Actions ─────────────────────────────────────────────────────────────── */
  const approve = async (row) => {
    setActioningId(row.id);
    setError("");
    setSuccess("");
    try {
      await api.post(`/quotations/${row.id}/approve`);
      setSuccess(`Quotation ${row.quotation_no} approved.`);
      setExpandedId(null);
      await loadPending();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Approval failed.");
    } finally {
      setActioningId(null);
    }
  };

  const reject = async (reason) => {
    if (!rejectRow) return;
    setActioningId(rejectRow.id);
    setError("");
    setSuccess("");
    try {
      await api.post(`/quotations/${rejectRow.id}/reject`, { reason });
      setSuccess(`Quotation ${rejectRow.quotation_no} rejected.`);
      setExpandedId(null);
      setRejectRow(null);
      await loadPending();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Rejection failed.");
    } finally {
      setActioningId(null);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  if (!isAdmin) {
    return (
      <div style={{
        borderRadius: 16, border: "1px solid #fde68a", background: "#fffbeb",
        padding: 20, fontSize: 14, color: "#92400e",
      }}>
        Quotation approvals are available to admin users only.
      </div>
    );
  }

  const totalHistoryPages = Math.ceil(historyTotal / perPage);

  return (
    <div>
      <RejectModal
        open={!!rejectRow}
        onClose={() => setRejectRow(null)}
        onConfirm={reject}
        loading={!!actioningId}
      />

      {/* Alerts */}
      {error && (
        <div style={{
          borderRadius: 12, border: "1px solid #fca5a5", background: "#fef2f2",
          padding: "12px 16px", fontSize: 13, color: "#dc2626", marginBottom: 16,
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          borderRadius: 12, border: "1px solid #86efac", background: "#f0fdf4",
          padding: "12px 16px", fontSize: 13, color: "#15803d", marginBottom: 16,
        }}>
          {success}
        </div>
      )}

      {/* Main Card */}
      <section style={{
        borderRadius: 20, border: "1px solid #e2e8f0", background: "#fff",
        boxShadow: "0 4px 16px rgba(15,23,42,.06)",
      }}>
        {/* Header with Tabs */}
        <div style={{
          padding: "20px 24px 0", borderBottom: "1px solid #e2e8f0",
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "flex-start",
            marginBottom: 16, flexWrap: "wrap", gap: 12,
          }}>
            <div>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: "0 0 4px" }}>
                Quotation Approvals
              </h3>
              <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                {activeTab === "pending"
                  ? "Review and approve or reject quotations awaiting admin decision."
                  : "Browse previously approved, rejected, and expired quotations."}
              </p>
            </div>
            <button
              onClick={() => activeTab === "pending" ? loadPending() : loadHistory(historyPage)}
              className="qa-btn qa-btn-outline"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>

          {/* Tab bar */}
          <div style={{ display: "flex", gap: 0 }}>
            <TabButton
              active={activeTab === "pending"}
              onClick={() => setActiveTab("pending")}
              icon={<Clock size={14} />}
              label="Pending Approvals"
              count={rows.length}
            />
            <TabButton
              active={activeTab === "history"}
              onClick={() => setActiveTab("history")}
              icon={<History size={14} />}
              label="Approval History"
            />
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "pending" && (
          <PendingTab
            rows={rows}
            loading={loading}
            expandedId={expandedId}
            actioningId={actioningId}
            onToggleExpand={toggleExpand}
            onApprove={approve}
            onReject={(row) => setRejectRow(row)}
          />
        )}

        {activeTab === "history" && (
          <HistoryTab
            rows={historyRows}
            loading={historyLoading}
            expandedId={expandedId}
            page={historyPage}
            totalPages={totalHistoryPages}
            total={historyTotal}
            filter={historyFilter}
            search={historySearch}
            dateFrom={historyDateFrom}
            dateTo={historyDateTo}
            onFilterChange={setHistoryFilter}
            onSearchChange={setHistorySearch}
            onDateFromChange={setHistoryDateFrom}
            onDateToChange={setHistoryDateTo}
            onPageChange={(p) => loadHistory(p)}
            onToggleExpand={toggleExpand}
            onApplyFilters={() => loadHistory(1)}
          />
        )}
      </section>

      <style>{styles}</style>
    </div>
  );
}

/* ── Tab Button ─────────────────────────────────────────────────────────────── */
function TabButton({ active, onClick, icon, label, count }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "10px 20px", fontSize: 13, fontWeight: 700,
        border: "none", borderBottom: active ? "3px solid #6366f1" : "3px solid transparent",
        background: "none", color: active ? "#6366f1" : "#64748b",
        cursor: "pointer", transition: "all .15s",
      }}
    >
      {icon} {label}
      {count != null && count > 0 && (
        <span style={{
          background: active ? "#6366f1" : "#94a3b8", color: "#fff",
          fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 10,
        }}>
          {count}
        </span>
      )}
    </button>
  );
}

/* ── Pending Tab ────────────────────────────────────────────────────────────── */
function PendingTab({ rows, loading, expandedId, actioningId, onToggleExpand, onApprove, onReject }) {
  if (loading) {
    return <div style={{ padding: "48px 24px", textAlign: "center", color: "#94a3b8" }}>Loading approval queue...</div>;
  }
  if (!rows.length) {
    return (
      <div style={{ padding: "48px 24px", textAlign: "center" }}>
        <CheckCircle2 size={40} style={{ color: "#86efac", marginBottom: 12 }} />
        <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>All caught up!</div>
        <div style={{ fontSize: 13, color: "#94a3b8" }}>No quotations are waiting for admin approval.</div>
      </div>
    );
  }

  return (
    <div>
      {rows.map((row) => {
        const isExpanded = expandedId === row.id;
        return (
          <div key={row.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
            {/* Collapsed row */}
            <div
              onClick={() => onToggleExpand(row.id)}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr auto 1fr auto auto",
                alignItems: "center",
                gap: 12,
                padding: "14px 24px",
                cursor: "pointer",
                background: isExpanded ? "#f8fafc" : "transparent",
                transition: "background .15s",
              }}
            >
              <div>
                <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 14 }}>{row.quotation_no}</div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>
                  {row.items_count} item{row.items_count !== 1 ? "s" : ""} · V{row.current_version || 1}
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 600, color: "#334155", fontSize: 13 }}>{row.customer_name}</div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>{row.salesperson ? `By ${row.salesperson}` : ""}</div>
              </div>
              <StatusBadge status={row.status} />
              <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 15 }}>
                ₹{fmt(row.total_amount)}
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{fmtDate(row.created_at)}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {!isExpanded && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); onApprove(row); }}
                      disabled={actioningId === row.id}
                      className="qa-btn qa-btn-approve-sm"
                    >
                      <CheckCircle2 size={13} /> Approve
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onReject(row); }}
                      disabled={actioningId === row.id}
                      className="qa-btn qa-btn-reject-sm"
                    >
                      <XCircle size={13} /> Reject
                    </button>
                  </>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleExpand(row.id); }}
                  className="qa-btn qa-btn-view-sm"
                >
                  <Eye size={13} />
                  {isExpanded ? "Hide" : "View"}
                </button>
              </div>
            </div>

            {/* Expanded detail */}
            {isExpanded && (
              <QuotationDetailPanel
                row={row}
                onApprove={onApprove}
                onReject={onReject}
                actioningId={actioningId}
                isHistory={false}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── History Tab ────────────────────────────────────────────────────────────── */
function HistoryTab({
  rows, loading, expandedId, page, totalPages, total,
  filter, search, dateFrom, dateTo,
  onFilterChange, onSearchChange, onDateFromChange, onDateToChange,
  onPageChange, onToggleExpand, onApplyFilters,
}) {
  return (
    <div>
      {/* Filters */}
      <div style={{
        padding: "16px 24px", borderBottom: "1px solid #f1f5f9",
        display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end",
      }}>
        <div>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 4 }}>Status</label>
          <select
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            style={filterInputStyle}
          >
            <option value="ALL">All Statuses</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected (Admin)</option>
            <option value="CLIENT_ACCEPTED">Client Accepted</option>
            <option value="CLIENT_REJECTED">Client Rejected</option>
            <option value="SENT_TO_CLIENT">Sent to Client</option>
            <option value="EXPIRED">Expired</option>
            <option value="DRAFT">Draft</option>
          </select>
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 4 }}>Search</label>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: 10, color: "#94a3b8" }} />
            <input
              type="text"
              placeholder="Customer or Quotation #"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onApplyFilters()}
              style={{ ...filterInputStyle, paddingLeft: 32, width: 220 }}
            />
          </div>
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 4 }}>From</label>
          <input type="date" value={dateFrom} onChange={(e) => onDateFromChange(e.target.value)} style={filterInputStyle} />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 4 }}>To</label>
          <input type="date" value={dateTo} onChange={(e) => onDateToChange(e.target.value)} style={filterInputStyle} />
        </div>
        <button onClick={onApplyFilters} className="qa-btn qa-btn-primary" style={{ height: 36 }}>
          <Filter size={13} /> Apply
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: "48px 24px", textAlign: "center", color: "#94a3b8" }}>Loading history...</div>
      ) : !rows.length ? (
        <div style={{ padding: "48px 24px", textAlign: "center" }}>
          <History size={40} style={{ color: "#cbd5e1", marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>No history found</div>
          <div style={{ fontSize: 13, color: "#94a3b8" }}>Try adjusting the filters above.</div>
        </div>
      ) : (
        <div>
          {rows.map((row) => {
            const isExpanded = expandedId === row.id;
            return (
              <div key={row.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <div
                  onClick={() => onToggleExpand(row.id)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr auto 1fr auto auto auto",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 24px",
                    cursor: "pointer",
                    background: isExpanded ? "#f8fafc" : "transparent",
                    transition: "background .15s",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 14 }}>{row.quotation_no}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{row.salesperson ? `By ${row.salesperson}` : ""}</div>
                  </div>
                  <div style={{ fontWeight: 600, color: "#334155", fontSize: 13 }}>{row.customer_name}</div>
                  <StatusBadge status={row.status} />
                  <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 15 }}>
                    ₹{fmt(row.total_amount)}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", textAlign: "center" }}>
                    <div>{row.approved_by || "-"}</div>
                    <div style={{ fontSize: 10, color: "#94a3b8" }}>{fmtDate(row.approved_at)}</div>
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>{fmtDate(row.created_at)}</div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); onToggleExpand(row.id); }}
                      className="qa-btn qa-btn-view-sm"
                    >
                      <Eye size={13} />
                      {isExpanded ? "Hide" : "View"}
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <QuotationDetailPanel row={row} isHistory={true} />
                )}
              </div>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "14px 24px", borderTop: "1px solid #e2e8f0",
            }}>
              <span style={{ fontSize: 13, color: "#64748b" }}>
                Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, total)} of {total}
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  onClick={() => onPageChange(page - 1)}
                  disabled={page <= 1}
                  className="qa-btn qa-btn-outline qa-btn-sm"
                >
                  <ChevronLeft size={14} /> Prev
                </button>
                <span style={{
                  padding: "6px 12px", fontSize: 13, fontWeight: 700, color: "#334155",
                }}>
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="qa-btn qa-btn-outline qa-btn-sm"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const filterInputStyle = {
  padding: "8px 12px",
  border: "1.5px solid #e2e8f0",
  borderRadius: 8,
  fontSize: 13,
  outline: "none",
  background: "#fff",
  color: "#1e293b",
  height: 36,
  boxSizing: "border-box",
};

const styles = `
  .qa-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border-radius: 10px; padding: 8px 16px; font-size: 13px; font-weight: 700;
    border: 1.5px solid #e2e8f0; background: #fff; color: #334155;
    cursor: pointer; transition: all .15s; white-space: nowrap;
  }
  .qa-btn:hover { opacity: .88; }
  .qa-btn:disabled { opacity: .5; cursor: not-allowed; }
  .qa-btn-outline { background: #fff; color: #334155; border-color: #cbd5e1; }
  .qa-btn-outline:hover { background: #f8fafc; }
  .qa-btn-primary { background: #6366f1; color: #fff; border-color: #6366f1; }
  .qa-btn-primary:hover { background: #4f46e5; }
  .qa-btn-success { background: #10b981; color: #fff; border-color: #10b981; }
  .qa-btn-success:hover { background: #059669; }
  .qa-btn-danger { background: #ef4444; color: #fff; border-color: #ef4444; }
  .qa-btn-danger:hover { background: #dc2626; }
  .qa-btn-approve-sm {
    padding: 6px 12px; font-size: 12px; border-radius: 8px;
    border-color: #86efac; color: #166534; background: #f0fdf4;
  }
  .qa-btn-approve-sm:hover { background: #dcfce7; }
  .qa-btn-reject-sm {
    padding: 6px 12px; font-size: 12px; border-radius: 8px;
    border-color: #fca5a5; color: #991b1b; background: #fef2f2;
  }
  .qa-btn-reject-sm:hover { background: #fee2e2; }
  .qa-btn-view-sm {
    padding: 6px 12px; font-size: 12px; border-radius: 8px;
    border-color: #c7d2fe; color: #4338ca; background: #eef2ff;
  }
  .qa-btn-view-sm:hover { background: #e0e7ff; }
  .qa-btn-sm { padding: 6px 12px; font-size: 12px; }

  @media (max-width: 768px) {
    .qa-btn { padding: 6px 12px; font-size: 12px; }
  }
`;
